import React, { useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';

/**
 * Structured Table Component
 * Renders data in a table format with optional sorting and filtering
 */
const StructuredTable = ({
  data = [],
  columns = [],
  title,
  sortable = false,
  filterable = false,
  maxRows = 100,
  className,
  style = {},
}) => {
  const { theme } = useAppTheme();
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = React.useState('');

  // Auto-generate columns if not provided
  const tableColumns = React.useMemo(() => {
    if (columns.length > 0) return columns;

    if (data.length === 0) return [];

    // Generate columns from first row
    const firstRow = data[0];
    return Object.keys(firstRow).map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      sortable: true,
    }));
  }, [data, columns]);

  // Filter data
  const filteredData = React.useMemo(() => {
    if (!filterText) return data;

    return data.filter((row) =>
      Object.values(row).some((value) => String(value).toLowerCase().includes(filterText.toLowerCase())),
    );
  }, [data, filterText]);

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Limit rows
  const displayData = sortedData.slice(0, maxRows);

  const handleSort = (key) => {
    if (!sortable) return;

    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderCell = (value, columnKey) => {
    if (value === null || value === undefined) {
      return <span style={styles.nullValue}>—</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <span
          style={{
            ...styles.booleanValue,
            color: value ? theme.colors.status.success : theme.colors.danger,
          }}
        >
          {value ? '✓' : '✗'}
        </span>
      );
    }

    if (typeof value === 'number') {
      return <span style={styles.numberValue}>{value.toLocaleString()}</span>;
    }

    if (typeof value === 'object') {
      return <span style={styles.objectValue}>{JSON.stringify(value)}</span>;
    }

    return <span style={styles.textValue}>{String(value)}</span>;
  };

  const getSortIcon = (columnKey) => {
    if (!sortable) return null;
    if (sortConfig.key !== columnKey) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const styles = {
    container: {
      backgroundColor: theme.colors.background.sidebar,
      borderRadius: theme.borderRadius.md,
      border: `1px solid ${theme.colors.border}`,
      overflow: 'hidden',
      ...style,
    },
    header: {
      padding: theme.spacing.md,
      borderBottom: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background.main,
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.text.primary,
      margin: 0,
      marginBottom: filterable ? theme.spacing.sm : 0,
    },
    filterInput: {
      width: '100%',
      padding: theme.spacing.sm,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.colors.background.sidebar,
      color: theme.colors.text.primary,
      fontSize: '14px',
      outline: 'none',
    },
    tableContainer: {
      overflowX: 'auto',
      maxHeight: '500px',
      overflowY: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      textAlign: 'left',
      backgroundColor: theme.colors.background.main,
      color: theme.colors.text.secondary,
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      borderBottom: `1px solid ${theme.colors.border}`,
      position: 'sticky',
      top: 0,
      cursor: sortable ? 'pointer' : 'default',
      userSelect: 'none',
    },
    td: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      borderBottom: `1px solid ${theme.colors.border}`,
      fontSize: '14px',
      verticalAlign: 'top',
    },
    nullValue: {
      color: theme.colors.text.muted,
      fontStyle: 'italic',
    },
    booleanValue: {
      fontWeight: '600',
      fontSize: '16px',
    },
    numberValue: {
      color: theme.colors.status.info,
      fontFamily: 'monospace',
    },
    textValue: {
      color: theme.colors.text.primary,
    },
    objectValue: {
      color: theme.colors.text.muted,
      fontFamily: 'monospace',
      fontSize: '12px',
    },
    emptyState: {
      padding: theme.spacing.xl,
      textAlign: 'center',
      color: theme.colors.text.muted,
      fontStyle: 'italic',
    },
    rowLimit: {
      padding: theme.spacing.sm,
      textAlign: 'center',
      color: theme.colors.text.muted,
      fontSize: '12px',
      backgroundColor: theme.colors.background.main,
      borderTop: `1px solid ${theme.colors.border}`,
    },
  };

  if (data.length === 0) {
    return (
      <div style={styles.container} className={className}>
        {title && (
          <div style={styles.header}>
            <h3 style={styles.title}>{title}</h3>
          </div>
        )}
        <div style={styles.emptyState}>No data to display</div>
      </div>
    );
  }

  return (
    <div style={styles.container} className={className}>
      {(title || filterable) && (
        <div style={styles.header}>
          {title && <h3 style={styles.title}>{title}</h3>}
          {filterable && (
            <input
              type="text"
              placeholder="Filter table..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={styles.filterInput}
            />
          )}
        </div>
      )}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              {tableColumns.map((column) => (
                <th
                  key={column.key}
                  style={styles.th}
                  onClick={() => handleSort(column.key)}
                  title={sortable ? `Click to sort by ${column.label}` : column.label}
                >
                  {column.label}
                  {getSortIcon(column.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, index) => (
              <tr key={index}>
                {tableColumns.map((column) => (
                  <td key={column.key} style={styles.td}>
                    {renderCell(row[column.key], column.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedData.length > maxRows && (
        <div style={styles.rowLimit}>
          Showing {displayData.length} of {sortedData.length} rows
        </div>
      )}
    </div>
  );
};

export default StructuredTable;
